import { useMemo } from 'react';

import {
  Button,
  type ButtonProps,
  Flex,
  HStack,
  PopoverContent,
  type PopoverContentProps,
  forwardRef,
  useControllableState,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  type DateRange,
  type DayModifiers,
  type DayPickerProps,
  type Matcher,
  type ModifiersStyles,
  DayPicker as ReactDayPicker,
} from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Caption, Day } from '@/components/DayPicker/_partials';
import {
  DEFAULT_MODIFIERS,
  DEFAULT_MODIFIERS_STYLES,
} from '@/components/DayPicker/constants';
import { useDayPickerMonthNavigation } from '@/components/DayPicker/hooks/useDayPickerMonthNavigation';
import { MonthPicker } from '@/components/MonthPicker';
import i18n from '@/lib/i18n/client';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';

dayjs.extend(utc);

export const dateRangeDefaultValue: DateRange = {
  from: undefined,
  to: undefined,
};

type InputDayPickerBaseProps = {
  submitButtonProps?: Omit<ButtonProps, 'onClick'>;
  isLoading?: boolean;
  notBefore?: Date;
  notAfter?: Date;
  wrappedInPopoverContent?: boolean;
  canDeselectSelectedDate?: boolean;
  isFilter?: boolean;
};

type InputSingleDayPickerProps = InputDayPickerBaseProps & {
  dayPickerMode: 'single';
  singleDayValue?: Date;
  defaultSingleDayValue?: Date;
  setSingleDayValue?: (singleDayValue: Date | undefined) => void;
  onSubmit?: (date: Date | undefined) => void;
};

type InputRangeDayPickerProps = InputDayPickerBaseProps & {
  dayPickerMode: 'range';
  dateRangeValue?: DateRange;
  defaultDateRangeValue?: DateRange;
  setDateRangeValue?: (rangeDate: DateRange | undefined) => void;
  onSubmit?: (rangeDate: DateRange | undefined) => void;
};

type InputDayPickerProps = InputSingleDayPickerProps | InputRangeDayPickerProps;

export type PopoverDayPickerProps = Omit<
  PopoverContentProps,
  keyof InputDayPickerProps
> &
  InputDayPickerProps;

export const PopoverDayPicker = forwardRef<PopoverDayPickerProps, 'section'>(
  (props, ref) => {
    const { t } = useTranslation();
    const hookMonthNavigation = useDayPickerMonthNavigation();
    const { setMonth, selectMonth, mode, toggleMode, month } =
      hookMonthNavigation;

    const [singleDayValue, setSingleDayValue] = useControllableState<
      Date | undefined
    >({
      value:
        props.dayPickerMode === 'single'
          ? dayjs(props.singleDayValue).utc().toDate()
          : undefined,
      defaultValue:
        props.dayPickerMode === 'single'
          ? dayjs(props.defaultSingleDayValue).utc().toDate()
          : undefined,
      onChange:
        props.dayPickerMode === 'single' ? props.setSingleDayValue : undefined,
    });

    const [dateRangeValue, setDateRangeValue] = useControllableState<
      DateRange | undefined
    >({
      value:
        props.dayPickerMode === 'range'
          ? {
              from: props?.dateRangeValue?.from
                ? dayjs(props?.dateRangeValue?.from).utc().toDate()
                : undefined,
              to: props?.dateRangeValue?.to
                ? dayjs(props?.dateRangeValue?.to).utc().toDate()
                : undefined,
            }
          : undefined,
      defaultValue:
        props.dayPickerMode === 'range'
          ? {
              from: props?.defaultDateRangeValue?.from
                ? dayjs(props.defaultDateRangeValue.from).utc().toDate()
                : undefined,
              to: props?.defaultDateRangeValue?.to
                ? dayjs(props.defaultDateRangeValue.to).utc().toDate()
                : undefined,
            }
          : undefined,
      onChange:
        props.dayPickerMode === 'range' ? props.setDateRangeValue : undefined,
    });

    const handleChangeMonth = (date?: Date | null) => {
      setMonth(date);
    };

    const handleSelectMonth = (date: Date) => {
      selectMonth(date);
    };

    const modifiers: DayModifiers = {
      ...DEFAULT_MODIFIERS,
    };

    const modifiersStyles: ModifiersStyles = {
      ...DEFAULT_MODIFIERS_STYLES,
    };

    const footer = (
      <HStack mt="2">
        <Button
          isDisabled={
            !props.canDeselectSelectedDate &&
            (props.dayPickerMode === 'single'
              ? !singleDayValue
              : !dateRangeValue)
            //     ||
            // (props.dayPickerMode === 'range' &&
            //   !!dateRangeValue?.from &&
            //   !dateRangeValue.to)
          }
          isLoading={props.isLoading}
          onClick={() => {
            if (props.dayPickerMode === 'single') {
              props.onSubmit?.(singleDayValue);
            } else {
              if (!!dateRangeValue?.from && !dateRangeValue.to) {
                props.onSubmit?.({
                  from: dateRangeValue.from,
                  to: dateRangeValue.from, // just for product requirement
                });
              } else {
                props.onSubmit?.(dateRangeValue);
              }
            }
          }}
          variant="@primary"
          size="sm"
          w="full"
          {...props.submitButtonProps}
        >
          {props.isFilter ? t('actions.applyFilter') : t('actions.done')}
        </Button>

        <Button
          isLoading={props.isLoading}
          size="sm"
          onClick={() => {
            if (props.dayPickerMode === 'single') {
              setSingleDayValue(undefined);
            } else {
              setDateRangeValue(dateRangeDefaultValue);
            }
          }}
        >
          {t('actions.clear')}
        </Button>
      </HStack>
    );

    const disabledDays = useMemo(() => {
      const disabledDays: Array<Matcher> = [];

      if (props.notBefore) {
        disabledDays.push({
          before: dayjs(props.notBefore).startOf('day').toDate(),
        });
      }

      if (props.notAfter) {
        disabledDays.push({
          after: dayjs(props.notAfter).startOf('day').toDate(),
        });
      }

      return disabledDays;
    }, [props.notAfter, props.notBefore]);

    const commonProps: DayPickerProps = {
      initialFocus: true,
      disabled: disabledDays,
      month: month ?? undefined,
      onMonthChange: handleChangeMonth,
      footer,
      components: {
        Caption: (props) => (
          <Caption
            {...props}
            onCaptionLabelClick={toggleMode}
            setMonth={(date) => {
              handleChangeMonth(date);
            }}
          />
        ),
        Day,
      },
      modifiers,
      modifiersStyles,
      locale: AVAILABLE_LANGUAGES.find((x) => x.key === i18n.language)
        ?.dataFnsLocale,
    };

    const handleSelectSingleDate = (selectedDate: Date | undefined) => {
      const utcDate = selectedDate
        ? dayjs(selectedDate).utc().toDate()
        : undefined;
      setSingleDayValue(utcDate);
      setMonth(utcDate);
    };

    const handleSelectDateRange = (selectedDayRange: DateRange | undefined) => {
      const utcRange = selectedDayRange
        ? {
            from: selectedDayRange.from
              ? dayjs(selectedDayRange.from).utc().toDate()
              : undefined,
            to: selectedDayRange.to
              ? dayjs(selectedDayRange.to).utc().toDate()
              : undefined,
          }
        : undefined;
      setDateRangeValue(utcRange);
      setMonth(utcRange?.from);
    };

    const mainContent = (
      <Flex py={2} px={1}>
        {mode === 'DAY' ? (
          props.dayPickerMode === 'single' ? (
            <ReactDayPicker
              {...commonProps}
              mode="single"
              selected={singleDayValue}
              onSelect={handleSelectSingleDate}
            />
          ) : (
            <ReactDayPicker
              {...commonProps}
              mode="range"
              selected={dateRangeValue}
              onSelect={handleSelectDateRange}
            />
          )
        ) : (
          <MonthPicker
            year={month?.getFullYear()}
            onMonthClick={handleSelectMonth}
            onTodayButtonClick={() =>
              handleSelectMonth(dayjs().startOf('day').toDate())
            }
          />
        )}
      </Flex>
    );

    if (!props.wrappedInPopoverContent) return mainContent;

    return (
      <PopoverContent
        ref={ref}
        shadow="md"
        width="auto"
        bg="white"
        borderRadius="xl"
        display="flex"
      >
        {mainContent}
      </PopoverContent>
    );
  }
);

PopoverDayPicker.displayName = 'PopoverDayPicker';
